import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: "AIzaSyDR9GHdBPg8J-1BGxUQcJ62FP1uQE73XDA",
  authDomain: "mandoobee-v2.firebaseapp.com",
  projectId: "mandoobee-v2",
  storageBucket: "mandoobee-v2.firebasestorage.app",
  messagingSenderId: "603341081852",
  appId: "1:603341081852:web:b4b2db391795c0890ddb79",
  measurementId: "G-9Y7QFK9906"
};

let app;
let messaging = null;
let serviceWorkerRegistration = null;
let foregroundMessageHandler = null;

// Simplified logging
const log = (message, data = null) => {
  console.log(`[FCM] ${message}`, data || '');
};

const initializeFirebase = async () => {
  try {
    log('Checking Firebase messaging support...');
    const supported = await isSupported();
    
    if (supported) {
      log('Firebase messaging supported');
      app = initializeApp(firebaseConfig);
      messaging = getMessaging(app);
      log('Firebase initialized successfully');
      return true;
    } else {
      log('Firebase messaging not supported in this browser');
      return false;
    }
  } catch (error) {
    log('Failed to initialize Firebase:', error);
    return false;
  }
};

export const registerServiceWorker = async () => {
  log('Starting service worker registration...');
  
  if (!('serviceWorker' in navigator)) {
    log('Service workers not supported');
    return null;
  }

  try {
    // Unregister any existing service workers to prevent conflicts
    const existingRegistrations = await navigator.serviceWorker.getRegistrations();
    for (let registration of existingRegistrations) {
      if (registration.scope.includes('firebase-messaging')) {
        await registration.unregister();
        log('Unregistered existing Firebase SW');
      }
    }

    // Register the service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/'
    });
    
    log('Service Worker registered successfully');
    
    // CRITICAL: Wait for service worker to be fully active
    if (registration.installing) {
      log('Service worker installing, waiting for activation...');
      await new Promise((resolve) => {
        registration.installing.addEventListener('statechange', (e) => {
          if (e.target.state === 'activated') {
            resolve();
          }
        });
      });
    }
    
    // Ensure service worker is ready and active
    await navigator.serviceWorker.ready;
    
    // Additional check to ensure pushManager is available
    if (!registration.pushManager) {
      throw new Error('PushManager not available on this registration');
    }
    
    log('Service worker is ready and active');
    serviceWorkerRegistration = registration;
    return registration;
  } catch (error) {
    log('Service Worker registration failed:', error);
    return null;
  }
};

export const setupForegroundMessageHandler = (callback) => {
  log('Setting up foreground message handler');
  
  if (!messaging) {
    log('Messaging not initialized');
    return null;
  }

  // Remove existing handler if any
  if (foregroundMessageHandler) {
    foregroundMessageHandler();
  }

  // Set up foreground message handler
  foregroundMessageHandler = onMessage(messaging, (payload) => {
    log('Foreground message received:', payload);
    
    if (callback) {
      callback(payload);
    }
  });

  log('Foreground message handler set up successfully');
  return foregroundMessageHandler;
};

export const requestNotificationPermission = async (foregroundMessageCallback) => {
  log('Starting notification permission request...');
  
  try {
    if (!("Notification" in window)) {
      throw new Error("This browser does not support notifications");
    }

    log(`Current notification permission: ${Notification.permission}`);

    // Initialize Firebase first
    const firebaseInitialized = await initializeFirebase();
    if (!firebaseInitialized) {
      throw new Error("Firebase messaging not supported");
    }

    // Register service worker and wait for it to be fully ready
    const registration = await registerServiceWorker();
    if (!registration) {
      throw new Error("Service worker registration failed");
    }

    // Additional wait to ensure everything is properly set up
    await new Promise(resolve => setTimeout(resolve, 1000));

    let permission = Notification.permission;

    // Request permission if needed
    if (permission === 'default') {
      log('Requesting browser notification permission...');
      permission = await Notification.requestPermission();
    }

    log(`Final notification permission: ${permission}`);

    if (permission === 'granted') {
      if (foregroundMessageCallback) {
        setupForegroundMessageHandler(foregroundMessageCallback);
      }

      log('Getting FCM token...');
      
      // Retry mechanism for getToken
      let token = null;
      let retries = 3;
      
      while (!token && retries > 0) {
        try {
          token = await getToken(messaging, {
            vapidKey: 'BJV_Cx43HWLGy9Z44CPsr9INzHfwKebkLKq8dhfBUDiGs615fsva15k7NSxj_5KaKLnrW2Y_RGr9h3vDJUeqzI8',
            serviceWorkerRegistration: registration
          });
          
          if (token) {
            break;
          }
        } catch (error) {
          log(`Token retrieval attempt failed (${4 - retries}/3):`, error);
          retries--;
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      if (token) {
        localStorage.setItem('fcm_token', token);
        log('FCM Token obtained and stored');
        return token;
      } else {
        throw new Error('Failed to get FCM token after multiple attempts');
      }
    } else {
      throw new Error('Notification permission not granted');
    }
  } catch (error) {
    log('Error in requestNotificationPermission:', error);
    throw error;
  }
};

export const getDebugInfo = () => {
  return {
    timestamp: new Date().toISOString(),
    browserSupport: {
      notifications: "Notification" in window,
      serviceWorker: "serviceWorker" in navigator,
      firebaseMessaging: !!messaging
    },
    permissions: {
      notification: Notification.permission
    },
    tokens: {
      current: localStorage.getItem('fcm_token')?.substring(0, 50) + '...' || 'None',
      hasToken: !!localStorage.getItem('fcm_token')
    },
    serviceWorker: {
      registered: !!serviceWorkerRegistration,
      ready: navigator.serviceWorker?.controller ? 'Active' : 'Not active',
      pushManagerAvailable: serviceWorkerRegistration?.pushManager ? 'Available' : 'Not available'
    }
  };
};

export const cleanupFirebaseMessaging = () => {
  log('Cleaning up Firebase messaging...');
  
  if (foregroundMessageHandler) {
    foregroundMessageHandler();
    foregroundMessageHandler = null;
  }
  
  log('Firebase messaging cleanup complete');
};

export { messaging };